import * as THREE from 'three';
import type { GeoColorScheme, GeoObjectTypes, GeoObjectMode, IGeoObject, Material, Materials, PreProcessingTypes } from '@visualizer/types';

interface GeoObjectOptions<T extends GeoObjectTypes> {
    name: string;
    colors: GeoColorScheme<T>;
    mode: GeoObjectMode;
    types: Array<T[number]>;
}

export abstract class GeoObject<T extends GeoObjectTypes> extends THREE.Group implements IGeoObject {
    protected materials: Materials<T>;
    private colors: GeoColorScheme<T>;
    private mode: GeoObjectMode;
    protected types: Array<T[number]>;

    protected constructor({ name, colors, mode, types }: GeoObjectOptions<T>) {
        super();
        this.name = name;
        this.types = types;

        this.colors = colors;
        this.mode = mode;

        this.materials = this.createMaterials();
    }

    abstract hasIndividualMeshes(): boolean;

    protected abstract createMaterials(): Materials<T>;

    getTypes(): PreProcessingTypes[] {
        return this.types;
    }

    setVisible(value: boolean): void {
        this.visible = value;
    }

    dispose(): void {
        Object.values<Material>(this.materials).forEach((e) => {
            e.stroke.dispose();
            e.material.dispose();
            e.shadowMaterial.dispose();
        });
    }

    /**
     * Changes the color scheme of the objects
     */
    changeColorScheme(colorScheme: GeoColorScheme<T>): void {
        this.colors = colorScheme;
        this.changeColors();
    }

    private changeColors(): void {
        for (const key of this.types) {
            const material = this.materials[key];
            const scheme = this.getMode() === 'render' ? this.colors[key].render : this.colors[key].standard;

            material.stroke.color.set(scheme.colorStroke);
            material.stroke.needsUpdate = true;
            material.material.color.set(scheme.color);
            material.material.needsUpdate = true;
            material.shadowMaterial.color.set(scheme.color);
            material.shadowMaterial.needsUpdate = true;
        }
    }

    getMode(): GeoObjectMode {
        return this.mode;
    }

    protected abstract onRenderMode(): void;
    protected abstract onStandardMode(): void;

    setMode(mode: GeoObjectMode): void {
        if (this.getMode() !== mode) {
            this.mode = mode;
        }

        // Re-apply visibility on mode-change:
        switch (mode) {
            case 'render':
                this.onRenderMode();
                break;
            case 'hybrid':
            case 'standard':
                this.onStandardMode();
                break;
        }
        this.changeColors();
    }

    isVisible(): boolean {
        return this.visible;
    }

    /**
     * Clones the meshes and returnes them with a name.
     */
    abstract export(): { name: string; mesh: THREE.Mesh }[];
}
